// /fiscal/recurring — agendamento de NF mensal sem cobrança.
// Lista + modal de criação + ações pausar/retomar/cancelar.

import * as Dialog from '@radix-ui/react-dialog';
import {
  AlertCircle,
  Ban,
  CalendarClock,
  Pause,
  Pencil,
  Play,
  Plus,
  Receipt,
  Trash2,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { formatBR, formatDayBR, formatBRDateTime, todayLocal } from '@control-center/shared';

import { cn } from '@control-center/ui';

import { ClientPicker } from '../../components/clients/ClientPicker';
import { Field, Input, Select, Textarea } from '../../components/clients/form-primitives';
import { PillButton } from '../../components/clients/PillButton';
import { EmptyState } from '../../components/common/EmptyState';
import {
  useCancelRecurringInvoice,
  useCreateRecurringInvoice,
  useDeleteRecurringInvoicePermanently,
  usePauseRecurringInvoice,
  useRecurringInvoices,
  useResumeRecurringInvoice,
  useUpdateRecurringInvoice,
} from '../../hooks/use-recurring-invoices';
import { extractErrorMessage } from '../../lib/errors';
import type { ClientEntity } from '../../lib/clients-api';
import type { RecurringInvoice, RecurringInvoiceStatus } from '../../lib/recurring-invoices-api';

const STATUS_LABEL: Record<RecurringInvoiceStatus, string> = {
  active: 'Ativo',
  paused: 'Pausado',
  cancelled: 'Cancelado',
};

const STATUS_TONE: Record<RecurringInvoiceStatus, string> = {
  active: 'bg-good/10 text-good border-good/40',
  paused: 'bg-warn/10 text-warn border-warn/40',
  cancelled: 'bg-panel-3 text-muted border-line',
};

function formatBRL(amount: string): string {
  const n = Number.parseFloat(amount);
  if (!Number.isFinite(n)) return amount;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function FiscalRecurringTab(): JSX.Element {
  const [statusFilter, setStatusFilter] = useState<RecurringInvoiceStatus | 'all'>('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState<RecurringInvoice | null>(null);

  const filters = statusFilter === 'all' ? {} : { status: statusFilter };
  const listQ = useRecurringInvoices(filters);

  const pause = usePauseRecurringInvoice();
  const resume = useResumeRecurringInvoice();
  const cancel = useCancelRecurringInvoice();
  const deletePermanently = useDeleteRecurringInvoicePermanently();

  async function handlePause(item: RecurringInvoice): Promise<void> {
    try {
      await pause.mutateAsync({ id: item.id, version: item.version });
      toast.success('Agendamento pausado.');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos pausar.'));
    }
  }

  async function handleResume(item: RecurringInvoice): Promise<void> {
    try {
      await resume.mutateAsync({ id: item.id, version: item.version });
      toast.success('Agendamento retomado.');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos retomar.'));
    }
  }

  async function handleCancel(item: RecurringInvoice): Promise<void> {
    const ok = window.confirm(
      `Cancelar agendamento de NF recorrente pra "${item.client?.name ?? 'cliente'}"?\n\nNenhuma NF futura será emitida. NFs já emitidas permanecem válidas.`,
    );
    if (!ok) return;
    try {
      await cancel.mutateAsync({ id: item.id, version: item.version });
      toast.success('Agendamento cancelado.');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos cancelar.'));
    }
  }

  async function handleDelete(item: RecurringInvoice): Promise<void> {
    const ok = window.confirm(
      `Excluir DEFINITIVAMENTE o agendamento de "${item.client?.name ?? 'cliente'}"?\n\nEle some da lista pra sempre. NFs já emitidas não são afetadas.`,
    );
    if (!ok) return;
    try {
      await deletePermanently.mutateAsync(item.id);
      toast.success('Agendamento excluído.');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos excluir.'));
    }
  }

  const items = listQ.data?.items ?? [];

  return (
    <div className="space-y-5">
      <p className="text-[13px] text-muted">
        Agenda NFS-e mensal pra serviços recebidos por fora do Asaas (Pix, dinheiro, transferência).
      </p>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[180px]">
            <Field label="Status">
              {({ inputId }) => (
                <Select
                  id={inputId}
                  value={statusFilter}
                  onChange={(e) =>
                    setStatusFilter(e.target.value as RecurringInvoiceStatus | 'all')
                  }
                >
                  <option value="all">Todos</option>
                  <option value="active">Ativos</option>
                  <option value="paused">Pausados</option>
                  <option value="cancelled">Cancelados</option>
                </Select>
              )}
            </Field>
          </div>
        </div>
        <PillButton
          solid
          icon={<Plus size={14} strokeWidth={2} />}
          onClick={() => setCreateOpen(true)}
        >
          Novo agendamento
        </PillButton>
      </div>

      {listQ.isLoading ? (
        <div className="overflow-hidden rounded-[14px] border border-line bg-panel">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              aria-hidden
              className="h-[68px] animate-pulse border-b border-line bg-panel-2 last:border-b-0"
            />
          ))}
        </div>
      ) : listQ.isError ? (
        <div className="rounded-[14px] border border-bad/40 bg-bad/5 p-5 text-[13px] text-bad">
          <div className="flex items-center gap-2 font-medium">
            <AlertCircle size={14} strokeWidth={2} />
            Não conseguimos carregar.
          </div>
          <p className="mt-1 text-muted">{extractErrorMessage(listQ.error, 'Tente recarregar.')}</p>
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={CalendarClock}
          title="Sem agendamentos de NF recorrente"
          description="Crie um agendamento pra emitir NFS-e automaticamente todo mês sem precisar de cobrança no Asaas."
          bullets={[
            'Cron emite todo dia 09:00 (BRT)',
            'Útil pra Pix/dinheiro recebido por fora',
            'Pause ou cancele quando quiser',
          ]}
          primaryAction={{
            label: 'Criar primeiro agendamento',
            icon: <Plus size={14} strokeWidth={2} />,
            onClick: () => setCreateOpen(true),
          }}
        />
      ) : (
        <div className="overflow-hidden rounded-[14px] border border-line bg-panel">
          <table className="w-full text-[13px]">
            <thead className="border-b border-line bg-panel-2">
              <tr className="text-left text-[11px] uppercase tracking-wider text-muted-3">
                <th className="px-4 py-3 font-medium">Cliente</th>
                <th className="px-4 py-3 font-medium tnum">Valor</th>
                <th className="px-4 py-3 font-medium">Dia</th>
                <th className="px-4 py-3 font-medium">Próxima</th>
                <th className="px-4 py-3 font-medium">Última</th>
                <th className="px-4 py-3 font-medium tnum">Emitidas</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-b border-line last:border-b-0 hover:bg-panel-2">
                  <td className="px-4 py-3">
                    <div className="flex items-start gap-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] bg-panel-3 text-muted">
                        <Receipt size={13} strokeWidth={1.8} />
                      </span>
                      <div className="min-w-0">
                        <div className="truncate font-medium text-ink">
                          {item.client?.name ?? '—'}
                        </div>
                        <p className="mt-[2px] truncate text-[11px] text-muted-2">
                          {item.serviceDescription}
                        </p>
                        {item.lastError && (
                          <p className="mt-[2px] truncate text-[11px] text-bad">
                            ⚠ Último erro: {item.lastError}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 tnum text-ink">{formatBRL(item.amount)}</td>
                  <td className="px-4 py-3 tnum text-muted">{item.billingDay}</td>
                  <td className="px-4 py-3 text-muted">{formatDayBR(item.nextEmissionDate)}</td>
                  <td className="px-4 py-3 text-[12px] text-muted-2">
                    {item.lastEmittedAt ? formatBRDateTime(item.lastEmittedAt) : '—'}
                  </td>
                  <td className="px-4 py-3 tnum text-ink-2">{item.emissionCount}</td>
                  <td className="px-4 py-3">
                    <span
                      className={cn(
                        'inline-flex items-center rounded-[6px] border px-2 py-[1px] text-[10px] font-medium uppercase tracking-wider',
                        STATUS_TONE[item.status],
                      )}
                    >
                      {STATUS_LABEL[item.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {item.status !== 'cancelled' && (
                        <button
                          type="button"
                          aria-label="Editar"
                          title="Editar valor, descrição ou dia"
                          onClick={() => setEditItem(item)}
                          className="flex h-8 w-8 items-center justify-center rounded-[6px] text-muted hover:bg-panel-3 hover:text-ink"
                        >
                          <Pencil size={13} strokeWidth={1.8} />
                        </button>
                      )}
                      {item.status === 'active' && (
                        <button
                          type="button"
                          aria-label="Pausar"
                          title="Pausar agendamento"
                          onClick={() => void handlePause(item)}
                          disabled={pause.isPending}
                          className="flex h-8 w-8 items-center justify-center rounded-[6px] text-muted hover:bg-panel-3 hover:text-warn disabled:opacity-40"
                        >
                          <Pause size={13} strokeWidth={1.8} />
                        </button>
                      )}
                      {item.status === 'paused' && (
                        <button
                          type="button"
                          aria-label="Retomar"
                          title="Retomar agendamento"
                          onClick={() => void handleResume(item)}
                          disabled={resume.isPending}
                          className="flex h-8 w-8 items-center justify-center rounded-[6px] text-muted hover:bg-panel-3 hover:text-good disabled:opacity-40"
                        >
                          <Play size={13} strokeWidth={1.8} />
                        </button>
                      )}
                      {item.status !== 'cancelled' && (
                        <button
                          type="button"
                          aria-label="Cancelar"
                          title="Cancelar (nenhuma NF futura será emitida)"
                          onClick={() => void handleCancel(item)}
                          disabled={cancel.isPending}
                          className="flex h-8 w-8 items-center justify-center rounded-[6px] text-muted hover:bg-panel-3 hover:text-bad disabled:opacity-40"
                        >
                          <Ban size={13} strokeWidth={1.8} />
                        </button>
                      )}
                      {item.status === 'cancelled' && (
                        <button
                          type="button"
                          aria-label="Excluir definitivamente"
                          title="Excluir definitivamente (some da lista)"
                          onClick={() => void handleDelete(item)}
                          disabled={deletePermanently.isPending}
                          className="flex h-8 w-8 items-center justify-center rounded-[6px] text-muted hover:bg-panel-3 hover:text-bad disabled:opacity-40"
                        >
                          <Trash2 size={13} strokeWidth={1.8} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateRecurringModal open={createOpen} onOpenChange={setCreateOpen} />
      <EditRecurringModal item={editItem} onClose={() => setEditItem(null)} />
    </div>
  );
}

// =============================================================================
// Modal de edição
// =============================================================================

function EditRecurringModal({
  item,
  onClose,
}: {
  item: RecurringInvoice | null;
  onClose: () => void;
}): JSX.Element {
  const [amount, setAmount] = useState('');
  const [serviceDescription, setServiceDescription] = useState('');
  const [billingDay, setBillingDay] = useState('');
  const [loadedId, setLoadedId] = useState<string | null>(null);

  const update = useUpdateRecurringInvoice();

  // Pré-preenche quando abre pra um item novo (sem useEffect — render-time sync)
  if (item && item.id !== loadedId) {
    setAmount(item.amount);
    setServiceDescription(item.serviceDescription);
    setBillingDay(String(item.billingDay));
    setLoadedId(item.id);
  }

  const amountNum = Number.parseFloat(amount);
  const amountValid = Number.isFinite(amountNum) && amountNum > 0;
  const descValid = serviceDescription.trim().length >= 10;
  const dayNum = Number.parseInt(billingDay, 10);
  const dayValid = Number.isInteger(dayNum) && dayNum >= 1 && dayNum <= 28;
  const isValid = amountValid && descValid && dayValid;

  function handleClose(): void {
    setLoadedId(null);
    onClose();
  }

  async function handleSubmit(): Promise<void> {
    if (!item || !isValid) return;
    try {
      await update.mutateAsync({
        id: item.id,
        payload: {
          version: item.version,
          amount,
          service_description: serviceDescription.trim(),
          billing_day: dayNum,
        },
      });
      toast.success('Agendamento atualizado.', {
        description:
          dayNum !== item.billingDay ? `Próxima emissão recalculada pro dia ${dayNum}.` : undefined,
      });
      handleClose();
    } catch (err) {
      toast.error('Não conseguimos salvar.', {
        description: extractErrorMessage(err, 'Tente novamente.'),
      });
    }
  }

  return (
    <Dialog.Root open={item !== null} onOpenChange={(open) => !open && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(520px,calc(100vw-32px))] max-h-[calc(100vh-48px)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-[14px] border border-line bg-panel p-6 shadow-lg focus:outline-none">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="text-[16px] font-semibold tracking-[-0.005em] text-ink">
                Editar agendamento
              </Dialog.Title>
              <Dialog.Description className="mt-[2px] text-[12px] text-muted">
                {item?.client?.name ?? 'Cliente'} — mudanças valem a partir da próxima emissão.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Fechar"
                className="flex h-8 w-8 items-center justify-center rounded-[6px] text-muted hover:bg-panel-2 hover:text-ink"
              >
                <X size={15} strokeWidth={2} />
              </button>
            </Dialog.Close>
          </div>

          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Valor (R$)" required hint="Ponto como separador — ex: 200.00">
                {({ inputId }) => (
                  <Input
                    id={inputId}
                    tabular
                    inputMode="decimal"
                    hasError={amount !== '' && !amountValid}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                )}
              </Field>
              <Field
                label="Dia do mês"
                required
                hint="1 a 28."
                error={billingDay !== '' && !dayValid ? 'Use um dia entre 1 e 28.' : undefined}
              >
                {({ inputId }) => (
                  <Input
                    id={inputId}
                    tabular
                    inputMode="numeric"
                    hasError={billingDay !== '' && !dayValid}
                    value={billingDay}
                    onChange={(e) => setBillingDay(e.target.value)}
                  />
                )}
              </Field>
            </div>

            <Field
              label="Descrição do serviço"
              required
              hint="Vira a Discriminação na NF. Mínimo 10 caracteres."
              error={serviceDescription !== '' && !descValid ? 'Mínimo 10 caracteres.' : undefined}
            >
              {({ inputId }) => (
                <Textarea
                  id={inputId}
                  rows={3}
                  hasError={serviceDescription !== '' && !descValid}
                  value={serviceDescription}
                  onChange={(e) => setServiceDescription(e.target.value)}
                />
              )}
            </Field>
          </div>

          <div className="mt-6 flex items-center justify-end gap-2">
            <PillButton onClick={handleClose} disabled={update.isPending}>
              Cancelar
            </PillButton>
            <PillButton
              solid
              onClick={() => void handleSubmit()}
              disabled={!isValid || update.isPending}
            >
              {update.isPending ? 'Salvando…' : 'Salvar'}
            </PillButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// =============================================================================
// Modal de criação
// =============================================================================

/**
 * Calcula próxima emissão estimada a partir de hoje + billing_day.
 * Replica a lógica do backend (calculateNextEmissionDate + advanceFromDate)
 * pra UI mostrar preview.
 */
function previewNextEmission(billingDay: number, emitNow: boolean): string {
  const today = todayLocal();
  const parts = today.split('-').map(Number);
  const y = parts[0] ?? 0;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  let targetY = y;
  let targetM = m;
  const shouldAdvance = billingDay <= d || emitNow;
  if (shouldAdvance) {
    targetM = m === 12 ? 1 : m + 1;
    targetY = m === 12 ? y + 1 : y;
  }
  return `${targetY}-${String(targetM).padStart(2, '0')}-${String(billingDay).padStart(2, '0')}`;
}

function CreateRecurringModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const [client, setClient] = useState<ClientEntity | null>(null);
  const [amount, setAmount] = useState('');
  const [serviceDescription, setServiceDescription] = useState('');
  const [billingDay, setBillingDay] = useState(5);
  const [emitNow, setEmitNow] = useState(false);

  const mutation = useCreateRecurringInvoice();

  function reset(): void {
    setClient(null);
    setAmount('');
    setServiceDescription('');
    setBillingDay(5);
    setEmitNow(false);
  }

  function handleOpenChange(next: boolean): void {
    if (!next) reset();
    onOpenChange(next);
  }

  const amountNum = Number.parseFloat(amount);
  const amountValid = Number.isFinite(amountNum) && amountNum > 0;
  const descValid = serviceDescription.trim().length >= 10;
  const dayValid = Number.isInteger(billingDay) && billingDay >= 1 && billingDay <= 28;
  const isValid = client !== null && amountValid && descValid && dayValid;

  async function handleSubmit(): Promise<void> {
    if (!client || !isValid) return;
    try {
      await mutation.mutateAsync({
        client_id: client.id,
        amount,
        service_description: serviceDescription.trim(),
        billing_day: billingDay,
        emit_now: emitNow,
      });
      toast.success(emitNow ? 'Agendamento criado + primeira NF emitida.' : 'Agendamento criado.', {
        description: `Próxima emissão calculada com base no dia ${billingDay}.`,
      });
      handleOpenChange(false);
    } catch (err) {
      toast.error('Não conseguimos criar o agendamento.', {
        description: extractErrorMessage(err, 'Tente novamente.'),
      });
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(560px,calc(100vw-32px))] max-h-[calc(100vh-48px)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-[14px] border border-line bg-panel p-6 shadow-lg focus:outline-none">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-brand-soft text-brand">
                <CalendarClock size={15} strokeWidth={1.8} />
              </span>
              <div>
                <Dialog.Title className="text-[16px] font-semibold tracking-[-0.005em] text-ink">
                  Novo agendamento de NF
                </Dialog.Title>
                <Dialog.Description className="mt-[2px] text-[12px] text-muted">
                  Define um cliente + valor + descrição que se repete todo mês.
                </Dialog.Description>
              </div>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Fechar"
                className="flex h-8 w-8 items-center justify-center rounded-[6px] text-muted hover:bg-panel-2 hover:text-ink"
              >
                <X size={15} strokeWidth={2} />
              </button>
            </Dialog.Close>
          </div>

          <div className="flex flex-col gap-4">
            <Field label="Cliente" required>
              {() => <ClientPicker value={client} onChange={setClient} />}
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Valor (R$)" required hint="Ponto como separador decimal — 1500.00">
                {({ inputId }) => (
                  <Input
                    id={inputId}
                    tabular
                    inputMode="decimal"
                    placeholder="0.00"
                    hasError={amount !== '' && !amountValid}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                )}
              </Field>

              <Field label="Dia do mês" required hint="1-28 (evita problemas de fevereiro)">
                {({ inputId }) => (
                  <Input
                    id={inputId}
                    tabular
                    type="number"
                    min={1}
                    max={28}
                    hasError={!dayValid}
                    value={billingDay}
                    onChange={(e) => setBillingDay(Number.parseInt(e.target.value || '0', 10))}
                  />
                )}
              </Field>
            </div>

            <Field
              label="Descrição do serviço"
              required
              hint="Vira a Discriminação na NF. Mínimo 10 caracteres."
              error={serviceDescription !== '' && !descValid ? 'Mínimo 10 caracteres.' : undefined}
            >
              {({ inputId }) => (
                <Textarea
                  id={inputId}
                  rows={3}
                  placeholder="Ex: Mensalidade de suporte técnico — plano Owl Suite"
                  hasError={serviceDescription !== '' && !descValid}
                  value={serviceDescription}
                  onChange={(e) => setServiceDescription(e.target.value)}
                />
              )}
            </Field>

            <label className="flex cursor-pointer items-start gap-3 rounded-[10px] border border-line bg-panel-2 p-3">
              <input
                type="checkbox"
                className="mt-[2px] h-4 w-4 rounded border-line bg-panel accent-ink"
                checked={emitNow}
                onChange={(e) => setEmitNow(e.target.checked)}
              />
              <div>
                <span className="text-[13px] font-medium text-ink">Emitir primeira NF agora</span>
                <p className="mt-[2px] text-[11px] text-muted">
                  Se ligado, a NF de hoje sai imediatamente e o próximo ciclo conta a partir do
                  próximo mês. Se desligado, a primeira sai só no próximo dia {billingDay}.
                </p>
              </div>
            </label>

            <div className="rounded-[10px] border border-line/60 bg-panel-2 p-3 text-[11px] text-muted-2">
              Hoje é <strong className="text-muted">{formatBR(todayLocal())}</strong>. Próxima
              emissão prevista:{' '}
              <strong className="text-muted">
                {formatBR(previewNextEmission(billingDay, emitNow))}
              </strong>
              .
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end gap-2">
            <PillButton onClick={() => handleOpenChange(false)} disabled={mutation.isPending}>
              Cancelar
            </PillButton>
            <PillButton
              solid
              onClick={() => void handleSubmit()}
              disabled={!isValid || mutation.isPending}
            >
              {mutation.isPending ? 'Criando…' : 'Criar agendamento'}
            </PillButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
